import React, { Suspense, useEffect, useState } from 'react';
import {
  Text,
  Spinner,
  Switch,
  RadioGroup,
  Radio,
  Field,
  Badge,
  makeStyles,
  shorthands,
  tokens,
} from '@fluentui/react-components';
import { RateTierMap, ELEVATION_BANDS } from '../components/RateTierMap';

const useStyles = makeStyles({
  page: {
    ...shorthands.padding('16px'),
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('16px'),
    minHeight: 0,
    flex: 1,
  },
  description: {
    color: tokens.colorNeutralForeground3,
    maxWidth: '720px',
  },
  mapCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('16px'),
    boxShadow: tokens.shadow4,
  },
  controls: {
    backgroundColor: tokens.colorNeutralBackground1,
    ...shorthands.borderRadius('4px'),
    ...shorthands.padding('12px', '16px'),
    boxShadow: tokens.shadow4,
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    ...shorthands.gap('24px'),
  },
  controlHead: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
  experimentNote: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    maxWidth: '720px',
  },
  fallback: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '400px',
  },
});

export default function SettingsTierMap() {
  const styles = useStyles();
  const [showElevation, setShowElevation] = useState(false);
  const [elevationFeet, setElevationFeet] = useState(ELEVATION_BANDS[0].feet);

  useEffect(() => {
    window.dispatchEvent(new CustomEvent('setPageContext', {
      detail: { title: 'Rate tier map' },
    }));
    return () => {
      window.dispatchEvent(new CustomEvent('setPageContext', { detail: null }));
    };
  }, []);

  return (
    <div className={styles.page}>
      <Text className={styles.description}>
        Each Puerto Rico municipio is assigned a rate tier (A–D) that drives roll-off,
        front-loader, and bulldog-bag pricing. Hover any municipio to see its tier.
      </Text>

      <div className={styles.controls}>
        <div className={styles.controlHead}>
          <Switch
            checked={showElevation}
            onChange={(_, d) => setShowElevation(d.checked)}
            label="High-elevation delivery zones"
          />
          <Badge appearance="tint" color="warning" size="small">
            Experiment
          </Badge>
        </div>
        <Field label="Elevation threshold" orientation="horizontal">
          <RadioGroup
            layout="horizontal"
            value={String(elevationFeet)}
            onChange={(_, d) => setElevationFeet(Number(d.value))}
            disabled={!showElevation}
          >
            {ELEVATION_BANDS.map((b) => (
              <Radio key={b.feet} value={String(b.feet)} label={`${b.feet.toLocaleString()} ft`} />
            ))}
          </RadioGroup>
        </Field>
      </div>

      {showElevation && (
        <Text className={styles.experimentNote}>
          The shaded geofence covers all terrain at or above the selected elevation
          (derived from public topographic data, projected onto the municipio map).
          It outlines where a mountain delivery charge could apply. This layer is a
          standalone experiment and is not wired into pricing.
        </Text>
      )}

      <div className={styles.mapCard}>
        <Suspense fallback={<div className={styles.fallback}><Spinner /></div>}>
          <RateTierMap
            compact
            showElevation={showElevation}
            elevationFeet={elevationFeet}
          />
        </Suspense>
      </div>
    </div>
  );
}
